/**
 * Tabs Parser Helper
 *
 * Special helper for parsing tabs components with tab-item children.
 * 
 * USAGE IN PARSER:
 * ===============
 * When the parser encounters a tabs component, it should use this helper
 * to create the proper structure with tab-item children.
 *
 * The parser.ts should be modified to check if component type is 'tabs'
 * and use `createTabsStructure` instead of the normal flow.
 *
 * EXAMPLE:
 * ```typescript
 * if (input.type === 'tabs') {
 *   return createTabsStructure(input as AiTabsInput, bodySettings)
 * }
 * ```
 */

import { v4 as uuidv4 } from 'uuid'
import type { IBody, IBodySettings } from '@makyoapp/makyo-sdk/models/AppBuilder.js'
import type { AiTabsInput } from './tabs.comp'
import type { AiComponentInput } from '../../registry'
import { TabsComp } from './tabs.comp'

/**
 * Tab Item Settings
 * These are the settings stored in _BodySettings for each tab-item
 */
export interface ITabItemSettings {
  title: string
  icon?: string
}

/**
 * Create a tab-item body entry
 */
function createTabItemBody(
  tabItemId: string,
  title: string,
  children: IBody[]
): IBody {
  return {
    _id: tabItemId,
    _ComponentID: 'tab-item',
    _GroupID: 'container',
    _Title: title,
    _Body: children,
    _Draggable: true,
    _Removable: true,
    _IsSubmit: false,
    _Actions: {},
    _Behavior: {
      _Metadata: {},
      _Visibility: {
        _Type: '',
        _Clauses: [],
      },
      _UserControlledFilter: {
        _Type: '',
        _FilterBy: '',
        _ShowOnPage: false,
        _ShowOnFilterPage: false,
        _TitleIcon: { value: '' },
        _AllowMultiple: false,
      },
    },
  }
}

/**
 * Create the full tabs structure with tab-item children
 * 
 * This function should be called by the parser when it encounters a tabs component.
 * It returns an IBody structure that includes:
 * - The tabs container
 * - Auto-generated tab-item children
 * - Recursively parsed children for each tab
 * 
 * @param input - The tabs AI input
 * @param bodySettings - The flat body settings object to populate
 * @param parseChildComponent - Function to recursively parse child components
 * @returns The tabs IBody structure with tab-item children
 */
export function createTabsStructure(
  input: AiTabsInput & { type: string; title?: string },
  bodySettings: IBodySettings,
  parseChildComponent: (child: AiComponentInput, settings: IBodySettings) => IBody
): IBody {
  // Generate tabs container ID
  const tabsId = uuidv4()

  // Parse tabs container settings
  const tabsComp = new TabsComp()
  const tabsSettings = tabsComp.parse(input)
  bodySettings[tabsId] = tabsSettings

  // Create tab-item bodies
  const tabItemBodies: IBody[] = []
  
  if (input.tabs && input.tabs.length > 0) {
    for (const tab of input.tabs) {
      const tabItemId = uuidv4()
      
      // Store tab-item settings
      const tabItemSettings: ITabItemSettings = {
        title: tab.title,
        icon: tab.icon,
      }
      bodySettings[tabItemId] = tabItemSettings

      // Parse children for this tab
      const tabChildren: IBody[] = []
      if (tab.children && tab.children.length > 0) {
        for (const childInput of tab.children) {
          const childBody = parseChildComponent(childInput, bodySettings)
          tabChildren.push(childBody)
        }
      }

      // Create tab-item body with children
      const tabItemBody = createTabItemBody(tabItemId, tab.title, tabChildren)
      tabItemBodies.push(tabItemBody)
    }
  }

  // Create tabs container body with tab-item children
  const tabsBody: IBody = {
    _id: tabsId,
    _ComponentID: 'tabs',
    _GroupID: 'container',
    _Title: input.title || '',
    _Body: tabItemBodies,
    _Draggable: true,
    _Removable: true,
    _IsSubmit: false,
    _Actions: {},
    _Behavior: {
      _Metadata: {},
      _Visibility: {
        _Type: '',
        _Clauses: [],
      },
      _UserControlledFilter: {
        _Type: '',
        _FilterBy: '',
        _ShowOnPage: false,
        _ShowOnFilterPage: false,
        _TitleIcon: { value: '' },
        _AllowMultiple: false,
      },
    },
  }

  return tabsBody
}

